import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, X } from 'lucide-react';
import { getProfileFields, getSearchTypeLabel, SEARCH_TYPES } from '../config/profileFields';
import { createProfile, updateProfile, getProfile } from '../lib/ProfileDataService';

const ProfileEdit = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  const [profileName, setProfileName] = useState('');
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState(SEARCH_TYPES.CONTACT);
  const [isNewProfile, setIsNewProfile] = useState(!profileId || profileId === 'new');

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const profile = await getProfile(profileId);
      
      setProfileName(profile.name || '');
      setFormData(profile.fields || {});
      setSearchType(profile.searchType || SEARCH_TYPES.CONTACT);
    } catch (error) {
      console.error('Error loading profile:', error);
      setErrors({ general: t('errorLoadingProfile') });
      // If profile not found, treat as new profile
      if (error.message.includes('404')) {
        setIsNewProfile(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileId && profileId !== 'new') {
      // Load existing profile data
      loadProfileData();
    } else {
      // For new profiles, ensure form data is empty
      setProfileName('');
      setFormData({});
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldId] || errors.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        delete newErrors.general; // Clear general error when user starts editing
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const fields = getProfileFields(searchType);
    const newErrors = {};

    // Validate profile name
    if (!profileName || profileName.trim() === '') {
      newErrors.profileName = t('fieldRequired');
    }

    fields.forEach(field => {
      if (field.required && (!formData[field.id] || formData[field.id].trim() === '')) {
        newErrors[field.id] = t('fieldRequired');
      }
      
      if (field.type === 'email' && formData[field.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.id])) {
          newErrors[field.id] = t('invalidEmail');
        }
      }
      
      if (field.type === 'url' && formData[field.id]) {
        try {
          new URL(formData[field.id]);
        } catch {
          newErrors[field.id] = t('invalidUrl');
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (isNewProfile) {
        await createProfile({ name: profileName, searchType, formData });
      } else {
        await updateProfile({ profileId, name: profileName, searchType, formData });
      }
      
      console.log('Profile saved successfully:', { searchType, formData });
      
      // Navigate back to profiles list
      navigate('/profiles');
    } catch (error) {
      console.error('Error saving profile:', error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/profiles');
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const error = errors[field.id];

    switch (field.type) {
      case 'select':
        return (
          <Select value={value} onValueChange={(val) => handleInputChange(field.id, val)}>
            <SelectTrigger className={error ? 'border-red-500' : ''}>
              <SelectValue placeholder={t(field.placeholder || field.label)} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={t(field.placeholder || field.label)}
            className={`${error ? 'border-red-500' : ''} ${isRTL ? 'text-right' : 'text-left'}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            rows={field.rows || 3}
          />
        );
      
      default:
        return (
          <Input
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={t(field.placeholder || field.label)}
            className={`${error ? 'border-red-500' : ''} ${isRTL ? 'text-right' : 'text-left'}`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        );
    }
  };

  const fields = getProfileFields(searchType);

  if (loading && !isNewProfile) {
    return (
      <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
            <ArrowLeft 
              className="w-6 h-6 cursor-pointer hover:text-primary" 
              onClick={handleCancel}
            />
            {isNewProfile ? t('newProfile') : t('editProfile')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('profileEditSubtitle')}
          </p>
        </div>
      </div>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t(getSearchTypeLabel(searchType))}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {loading ? t('saving') : t('save')}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}
          <form className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="profileName" className="text-sm font-medium">
                {t('profileName')}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Input
                id="profileName"
                value={profileName}
                onChange={(e) => {
                  setProfileName(e.target.value);
                  // Clear general error when user starts editing
                  if (errors.general) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.general;
                      return newErrors;
                    });
                  }
                }}
                placeholder={t('profileNamePlaceholder')}
                className={isRTL ? 'text-right' : 'text-left'}
                dir={isRTL ? 'rtl' : 'ltr'}
                required
              />
              {errors.profileName && (
                <p className="text-sm text-red-500">{errors.profileName}</p>
              )}
            </div>
            {fields.map((field) => (
              <div key={field.id} className="space-y-2">
                <label htmlFor={field.id} className="text-sm font-medium">
                  {t(field.label)}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderField(field)}
                {errors[field.id] && (
                  <p className="text-sm text-red-500">{errors[field.id]}</p>
                )}
              </div>
            ))}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileEdit; 